import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PaymentGateway from '../components/payment/PaymentGateway';
import consultationService from '../services/consultationService';
import VideoCalendarPicker from '../components/orders/Consultancy/VideoCalendarPicker';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { FaVideo, FaUserMd, FaClock, FaCalendarAlt, FaWhatsapp, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';

export default function VideoConsultation() {
  const { token, currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorsError, setDoctorsError] = useState(null);

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patientWhatsapp, setPatientWhatsapp] = useState('');
  const [consultationId, setConsultationId] = useState(null); // Store consultation ID after booking
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [offerInfo, setOfferInfo] = useState(null);
  const [activeOffers, setActiveOffers] = useState([]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Check if a doctor was passed via navigation state
    if (location.state?.selectedDoctor) {
      setSelectedDoctor(location.state.selectedDoctor);
    } else {
      // Only load all doctors if no specific doctor was selected
      loadDoctors();
    }
  }, [token, navigate, location.state]);

  // Fetch free-consultation offer info (generic on load; refined by doctor/date). Drives the banner.
  useEffect(() => {
    // Use the local YYYY-MM-DD the user picked (selectedSlot.date) — NOT a UTC-derived
    // date, which can shift to a different day and make the offer look inactive.
    let date;
    let time;
    if (selectedSlot?.date && /^\d{4}-\d{2}-\d{2}$/.test(selectedSlot.date)) {
      date = selectedSlot.date;
    } else if (selectedSlot?.datetime) {
      try { date = new Date(selectedSlot.datetime).toISOString().split('T')[0]; } catch (e) { /* ignore */ }
    }
    // Local wall-clock HH:MM from the slot's ISO datetime (it carries the +05:30 offset).
    if (selectedSlot?.datetime && selectedSlot.datetime.includes('T')) {
      try { time = selectedSlot.datetime.split('T')[1].slice(0, 5); } catch (e) { /* ignore */ }
    }
    let cancelled = false;
    consultationService
      .getVideoOfferInfo({ doctorId: selectedDoctor?.id, date, time })
      .then((info) => { if (!cancelled) setOfferInfo(info); });
    return () => { cancelled = true; };
  }, [selectedDoctor, selectedSlot]);

  // Fetch all currently-active offers (so users see what's running before booking).
  useEffect(() => {
    let cancelled = false;
    consultationService.getActiveVideoOffers().then((res) => {
      if (!cancelled) setActiveOffers(res?.offers || []);
    });
    return () => { cancelled = true; };
  }, []);

  const loadDoctors = async () => {
    setLoadingDoctors(true);
    setDoctorsError(null);

    try {
      const response = await consultationService.getOnlineDoctors();

      let doctorsArray = [];
      if (Array.isArray(response)) {
        doctorsArray = response;
      } else if (response && Array.isArray(response.doctors)) {
        doctorsArray = response.doctors;
      } else if (response && Array.isArray(response.data)) {
        doctorsArray = response.data;
      }

      setDoctors(doctorsArray);
    } catch (error) {
      console.error('Error loading doctors:', error);
      setDoctorsError(error.message || 'Failed to load doctors');
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedDoctor || !selectedSlot) {
      setBookingError('Please select a doctor and time slot');
      return;
    }

    if (!patientWhatsapp || !patientWhatsapp.trim()) {
      setBookingError('WhatsApp number is required for video consultation link');
      return;
    }

    if (!/^\d{10}$/.test(patientWhatsapp.replace(/\D/g, ''))) {
      setBookingError('Please enter a valid 10-digit WhatsApp number');
      return;
    }

    setBookingLoading(true);
    setBookingError(null);

    try {
      // Book video consultation first
      const response = await consultationService.bookVideoConsultation({
        doctor_id: selectedDoctor.id,
        slot_time: selectedSlot.datetime,
        price: selectedDoctor.price_video || 500,
        patient_whatsapp: patientWhatsapp.replace(/\D/g, ''),
      });

      // Store consultation ID for payment
      const consId = response.data?.consultation_id || response.consultation_id || response.id;
      setConsultationId(consId);

      setBookingLoading(false);

      // Free offer bookings are confirmed immediately by the server (no payment).
      const isFree = response.is_free ?? response.data?.is_free;
      const paymentRequired = response.payment_required ?? response.data?.payment_required;
      if (isFree || paymentRequired === false) {
        setBookingConfirmed(true);
        return;
      }

      // Paid bookings: show the payment gateway.
      setShowPayment(true);
    } catch (error) {
      console.error('Booking error:', error);
      setBookingError(error.message || 'Failed to book consultation');
      setBookingLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    // Payment is complete, consultation already booked and will be updated via webhook
    // Just navigate to success
    setBookingConfirmed(true);
  };

  const handlePaymentError = (error) => {
    setPaymentError(error?.message || error || 'Payment failed. Please try again.');
    setShowPayment(false);
  };

  // Effective pricing for the selected doctor/slot, reflecting any live offer
  // (primary discount, or the fallback once the quota is used up).
  const normalFee = selectedDoctor?.price_video || 500;
  const offerEligible = !!(offerInfo?.active && offerInfo?.slots_available);
  const effectiveFee = offerEligible && offerInfo?.offer_price != null ? offerInfo.offer_price : normalFee;
  const isFreeEligible = offerEligible && (offerInfo?.effective_free || Number(effectiveFee) === 0);
  const hasBenefit = offerEligible && Number(effectiveFee) < Number(normalFee); // a real discount (incl. free)
  const offerLabel = offerInfo?.effective_label;

  if (bookingConfirmed) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Your video consultation has been successfully booked with Dr. {selectedDoctor?.name}.
              </p>
              <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-4">Consultation Details:</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <FaUserMd className="text-blue-600" />
                    <span className="text-gray-700">Dr. {selectedDoctor?.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaCalendarAlt className="text-blue-600" />
                    <span className="text-gray-700">{selectedSlot?.date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaClock className="text-blue-600" />
                    <span className="text-gray-700">{selectedSlot?.time}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaWhatsapp className="text-blue-600" />
                    <span className="text-gray-700">{patientWhatsapp}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                A confirmation message and video call link will be sent to your WhatsApp number.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors font-semibold"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-4"
            >
              <FaArrowLeft /> Back
            </button>
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <FaVideo className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold">Video Consultation</h1>
                  <p className="text-teal-100">
                    {location.state?.selectedDoctor
                      ? `Book your appointment with Dr. ${location.state.selectedDoctor.name}`
                      : 'Connect with our medical specialists from anywhere'
                    }
                  </p>
                </div>
              </div>
              {location.state?.selectedDoctor && (
                <div className="mt-4 bg-white bg-opacity-20 rounded-lg p-4">
                  <p className="text-sm text-white flex items-center gap-2">
                    <FaCheckCircle className="w-4 h-4" />
                    Complete the booking by selecting a date, time slot, and providing your WhatsApp number
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Current offers — what's running right now (visible before booking) */}
          {activeOffers.length > 0 && (
            <div
              className="mb-6 rounded-xl p-5 border-2 shadow-md"
              style={{ background: 'linear-gradient(135deg,#fef3c7,#fde68a)', borderColor: '#f59e0b' }}
            >
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-2xl">🎁</span> Current Offers
              </h3>
              <div className="space-y-2">
                {activeOffers.map((o) => (
                  <div key={o.id} className="flex items-start gap-3 bg-white bg-opacity-70 rounded-lg p-3">
                    <span className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-bold whitespace-nowrap">
                      {o.discount_label}
                    </span>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{o.title}</div>
                      {o.description && <div className="text-sm text-gray-600">{o.description}</div>}
                      <div className="text-xs text-gray-700 mt-1">
                        {o.within_quota
                          ? `${o.free_slots_remaining} slot(s) left today`
                          : o.slots_available
                            ? 'Free quota used up — reduced/normal price now'
                            : "Today's slots are full"}
                        {o.time_window && o.time_window !== 'All day' ? ` · ${o.time_window}` : ''}
                        {o.doctors && o.doctors.length > 0 ? ` · for ${o.doctors.join(', ')}` : ' · all doctors'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-start gap-2 bg-white bg-opacity-60 rounded-lg px-3 py-2">
                <span>🕒</span>
                <p className="text-xs text-amber-900 font-semibold m-0">
                  Select a date &amp; time slot below to check if the offer applies to your booking.
                  Offers run only during the shown hours and for a limited number of users —
                  your final price appears in the <span className="underline">Booking Summary</span>.
                </p>
              </div>
            </div>
          )}

          {/* When a slot is picked but the offer didn't apply to it, explain why */}
          {selectedDoctor && selectedSlot && offerInfo && !hasBenefit && activeOffers.length > 0 && (
            <div
              className="mb-6 rounded-xl px-4 py-3 border shadow-sm text-sm"
              style={{ background: '#fff7ed', borderColor: '#fdba74', color: '#9a3412' }}
            >
              ℹ️ The offer doesn’t apply to the slot you selected — it may be outside the offer hours,
              or the free quota is used up. This slot is at the normal fee; try a slot within the offer time shown above.
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Doctor Selection */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaUserMd className="text-teal-600" />
                  Select Specialist
                </h2>

                {loadingDoctors ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading doctors...</p>
                  </div>
                ) : doctorsError ? (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                    {doctorsError}
                  </div>
                ) : !selectedDoctor ? (
                  <div className="space-y-3">
                    {!Array.isArray(doctors) || doctors.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">No doctors available at the moment</p>
                      </div>
                    ) : (
                      doctors.map((doc) => (
                        <div
                          key={doc.id}
                          onClick={() => setSelectedDoctor(doc)}
                          className="p-4 rounded-lg border-2 border-gray-200 hover:border-teal-500 bg-white hover:bg-teal-50 cursor-pointer transition-all"
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <FaUserMd className="text-teal-600 text-xl" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">Dr. {doc.name}</h3>
                              <p className="text-sm text-teal-600 font-medium">{doc.specialty || doc.specialization}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <FaClock className="text-gray-400" />
                                  30 min session
                                </span>
                                <span className="font-semibold text-teal-600">₹{doc.price_video || 500}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-300 rounded-lg p-5">
                    {location.state?.selectedDoctor && (
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full">
                          <FaCheckCircle className="w-3 h-3" />
                          Selected Doctor
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                          <FaUserMd className="text-white text-2xl" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">Dr. {selectedDoctor.name}</h3>
                          <p className="text-sm text-teal-700 font-medium mb-1">{selectedDoctor.specialty || selectedDoctor.specialization}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <FaClock className="text-teal-500" />
                              30 min session
                            </span>
                            <span className="font-bold text-teal-700">₹{selectedDoctor.price_video || 500}</span>
                          </div>
                        </div>
                      </div>
                      {!location.state?.selectedDoctor && (
                        <button
                          onClick={() => setSelectedDoctor(null)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm underline"
                        >
                          Change
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Slot Selection */}
              {selectedDoctor && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-teal-600" />
                    Select Date & Time
                  </h2>
                  <VideoCalendarPicker
                    doctorId={selectedDoctor.id}
                    onSlotSelect={setSelectedSlot}
                    consultationService={consultationService}
                  />
                </div>
              )}

              {/* WhatsApp Number */}
              {selectedDoctor && selectedSlot && (
                <div className="bg-white rounded-xl shadow-md p-6 border-2 border-green-200 transition-all duration-500 ease-in-out">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaWhatsapp className="text-green-600 text-2xl" />
                    WhatsApp Number
                  </h2>
                  <div className="mb-4">
                    <input
                      type="tel"
                      placeholder="Enter 10-digit WhatsApp number"
                      value={patientWhatsapp}
                      onChange={(e) => setPatientWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-lg"
                      autoFocus
                    />
                    {patientWhatsapp && patientWhatsapp.length === 10 && (
                      <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                        <FaCheckCircle />
                        <span>Valid WhatsApp number</span>
                      </div>
                    )}
                    {patientWhatsapp && patientWhatsapp.length < 10 && (
                      <div className="flex items-center gap-2 mt-2 text-orange-600 text-sm">
                        <FaClock />
                        <span>{10 - patientWhatsapp.length} more digit{10 - patientWhatsapp.length > 1 ? 's' : ''} required</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800 flex items-start gap-2">
                      <FaWhatsapp className="mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Important:</strong> Video call link will be sent to this WhatsApp number 15 minutes before your scheduled appointment
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {bookingError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                  {bookingError}
                </div>
              )}

              {/* Payment Error */}
              {paymentError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                  {paymentError}
                </div>
              )}
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border-2 border-teal-200 p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Booking Summary</h3>
                  <div className="flex gap-1">
                    <div className={`w-2 h-2 rounded-full ${selectedDoctor ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                    <div className={`w-2 h-2 rounded-full ${selectedSlot ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                    <div className={`w-2 h-2 rounded-full ${patientWhatsapp && patientWhatsapp.length === 10 ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Doctor */}
                  <div className={`p-3 rounded-lg ${selectedDoctor ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <FaUserMd className={selectedDoctor ? 'text-teal-600' : 'text-gray-400'} />
                      <p className="text-sm font-medium text-gray-600">Doctor</p>
                    </div>
                    {selectedDoctor ? (
                      <p className="font-semibold text-gray-900 ml-6">Dr. {selectedDoctor.name}</p>
                    ) : (
                      <p className="text-sm text-gray-500 ml-6">Not selected</p>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className={`p-3 rounded-lg ${selectedSlot ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <FaCalendarAlt className={selectedSlot ? 'text-teal-600' : 'text-gray-400'} />
                      <p className="text-sm font-medium text-gray-600">Date & Time</p>
                    </div>
                    {selectedSlot ? (
                      <>
                        <p className="font-semibold text-gray-900 ml-6">{selectedSlot.date}</p>
                        <p className="text-sm text-gray-700 ml-6">{selectedSlot.time}</p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 ml-6">Not selected</p>
                    )}
                  </div>

                  {/* WhatsApp */}
                  <div className={`p-3 rounded-lg ${patientWhatsapp && patientWhatsapp.length === 10 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <FaWhatsapp className={patientWhatsapp && patientWhatsapp.length === 10 ? 'text-green-600' : 'text-gray-400'} />
                      <p className="text-sm font-medium text-gray-600">WhatsApp</p>
                    </div>
                    {patientWhatsapp && patientWhatsapp.length === 10 ? (
                      <p className="font-semibold text-gray-900 ml-6">{patientWhatsapp}</p>
                    ) : (
                      <p className="text-sm text-gray-500 ml-6">Not provided</p>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Consultation Fee</span>
                    {isFreeEligible ? (
                      <span className="text-2xl font-bold text-green-600">
                        <span className="text-lg line-through text-gray-400 mr-2">₹{normalFee}</span>FREE
                      </span>
                    ) : hasBenefit ? (
                      <span className="text-2xl font-bold text-teal-600">
                        <span className="text-lg line-through text-gray-400 mr-2">₹{normalFee}</span>₹{effectiveFee}
                      </span>
                    ) : (
                      <span className="text-2xl font-bold text-teal-600">₹{normalFee}</span>
                    )}
                  </div>
                  {hasBenefit && offerLabel && (
                    <p className="text-xs text-green-700 font-semibold mt-1 text-right">
                      🎉 {offerInfo.title} · {offerLabel} applied
                    </p>
                  )}
                </div>

                {!showPayment ? (
                  <button
                    onClick={handleBooking}
                    disabled={!selectedDoctor || !selectedSlot || !patientWhatsapp || bookingLoading}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bookingLoading
                      ? 'Processing...'
                      : isFreeEligible
                        ? 'Book Free Consultation'
                        : `Proceed to Payment (₹${effectiveFee})`}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <PaymentGateway
                      amount={effectiveFee}
                      serviceType="online_consultation"
                      serviceId={consultationId}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      customerDetails={{
                        name: currentUser?.name || '',
                        email: currentUser?.email || '',
                        mobile: patientWhatsapp || currentUser?.mobile || ''
                      }}
                    />
                    <button
                      onClick={() => setShowPayment(false)}
                      className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
